/* eslint-disable no-await-in-loop */
import { useEffect, useState } from "react";
import { API } from "aws-amplify";
import { useHistory } from "react-router-dom";
import {
  getCustomerWithBaselinesAndStageTypes,
  listFeedsV1andV2,
} from "../../../../graphql/queries";
import {
  ReportingType,
  CompoundFeed,
  CompoundFeedV2,
  ModelCompoundFeedConnection,
  ModelCompoundFeedConnectionV2,
  Customer,
  Farm,
} from "../../../../graphql/types";
import { isShrimp } from "../../helpers/animals";
import { FootprintModel } from "../../models/Footprint";
import {
  DatasetOption,
  FootprintCompareOptions,
  formatFarmsData,
  buildFootprintURL,
  formatFeedsData,
  isFeedDataset,
  isFarmDataset,
} from "../../utils/footprint-compare-utils";
import featureFlags from "../../utils/featureFlags";

interface UseFootprintCompareProps {
  footprint: FootprintModel;
  customerId: string;
  type: string;
  comparisonReference?: string;
}

const fetchFeedsData = async (customerId: string) => {
  let nextToken: string | null = null;

  const feedsV1: CompoundFeed[] = [];
  const feedsV2: CompoundFeedV2[] = [];

  do {
    const queryWithVars = {
      query: listFeedsV1andV2,
      variables: {
        customerId,
        nextToken,
      },
    };

    const result = (await API.graphql(queryWithVars)) as {
      data: {
        getCompoundFeedList?: ModelCompoundFeedConnection | null;
        listCompoundFeedsV2?: ModelCompoundFeedConnectionV2 | null;
      };
    };

    if (result.data?.getCompoundFeedList?.items) {
      feedsV1.push(
        ...result.data.getCompoundFeedList.items.filter(
          (item): item is CompoundFeed => item !== null,
        ),
      );
    }

    if (result.data?.listCompoundFeedsV2?.items) {
      feedsV2.push(
        ...result.data.listCompoundFeedsV2.items.filter(
          (item): item is CompoundFeedV2 => item !== null,
        ),
      );
    }

    nextToken = result.data?.listCompoundFeedsV2?.nextToken ?? null;
  } while (nextToken);

  const data = formatFeedsData(customerId, feedsV1, feedsV2);

  return data;
};

const fetchFarmsData = async (customerId: string) => {
  const queryWithVars = {
    query: getCustomerWithBaselinesAndStageTypes,
    variables: {
      id: customerId,
    },
  };

  const result = (await API.graphql(queryWithVars)) as {
    data: {
      getCustomer?: Customer | null;
    };
  };

  const farms =
    result.data?.getCustomer?.farms?.items?.filter(
      (item): item is Farm => item !== null,
    ) ?? [];

  const data = formatFarmsData(customerId, farms);

  return data;
};

const useFootprintCompare = ({
  footprint,
  customerId,
  type,
  comparisonReference,
}: UseFootprintCompareProps) => {
  const [datasets, setDatasets] = useState<FootprintCompareOptions[]>([]);
  const [comparisonDatasets, setComparisonDatasets] = useState<
    FootprintCompareOptions[]
  >([]);

  const [baseline, setBaseline] = useState<DatasetOption>();
  const [comparison, setComparison] = useState<DatasetOption>();

  const [isComparing, setIsComparing] = useState<boolean>(
    !!comparisonReference,
  );

  const history = useHistory();

  const navigateToFootprint = (
    baselineDataset: DatasetOption,
    comparisonDataset?: DatasetOption,
    comparisonSelectedStage?: string,
  ) => {
    const { path, variables } = buildFootprintURL(
      baselineDataset,
      comparisonDataset,
      comparisonSelectedStage,
    );
    history.push(path, variables);
  };

  const findDataset = (reference: string, data?: FootprintCompareOptions[]) => {
    const source = data ?? datasets;
    const allOptions = source.flatMap((dataset) => dataset.options);

    return (
      allOptions.find((option) => option.footprintReference === reference) ??
      allOptions[0]
    );
  };

  const filterComparisonDatasets = (
    baselineDataset: DatasetOption | undefined,
    data?: FootprintCompareOptions[],
  ): FootprintCompareOptions[] => {
    if (!baselineDataset) {
      return [];
    }

    const source = data ?? datasets;

    if (isFeedDataset(baselineDataset)) {
      return source;
    }

    return source
      .map((dataset) => ({
        label: dataset.label,
        options: dataset.options.filter((option) => {
          if (!isFarmDataset(option)) {
            return false;
          }

          const isSameAnimalType =
            option.animalType === baselineDataset.animalType;
          const isSameReportingType =
            option.reportingType ===
            (baselineDataset.reportingType ?? ReportingType.FullLca);

          if (!isSameAnimalType || !isSameReportingType) {
            return false;
          }

          if (isShrimp(baselineDataset.animalType)) {
            return option.stageType === baselineDataset.stageType;
          }

          return true;
        }),
      }))
      .filter((dataset) => dataset.options.length > 0);
  };

  useEffect(() => {
    const processData = (data: FootprintCompareOptions[]) => {
      setDatasets(data);

      const baselineDataset = findDataset(footprint.reference, data);

      if (baselineDataset) {
        setBaseline(baselineDataset);

        const compatibleComparisons = filterComparisonDatasets(
          baselineDataset,
          data,
        );

        setComparisonDatasets(compatibleComparisons);

        if (comparisonReference) {
          const comparisonDataset = findDataset(comparisonReference, data);

          if (comparisonDataset) {
            const isInCompatibleComparisons = compatibleComparisons.some(
              (dataset) =>
                dataset.options.some(
                  (option) =>
                    option.footprintReference ===
                    comparisonDataset.footprintReference,
                ),
            );

            if (isInCompatibleComparisons) {
              setIsComparing(true);
              setComparison(comparisonDataset);
            }
          }
        }
      }
    };

    if (datasets.length === 0) {
      const loadData = async () => {
        try {
          let data: FootprintCompareOptions[];

          if (type === "f") {
            data = await fetchFeedsData(customerId);
          } else {
            data = await fetchFarmsData(customerId);
          }

          processData(data);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error loading data:", error);
        }
      };

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, footprint.reference, comparisonReference, type]);

  function handleBaselineChange(newBaseline: string) {
    const selectedBaseline = findDataset(newBaseline);
    setBaseline(selectedBaseline);

    const compatibleComparisons = filterComparisonDatasets(selectedBaseline);
    setComparisonDatasets(compatibleComparisons);

    setComparison(undefined);

    if (selectedBaseline) {
      navigateToFootprint(selectedBaseline);
    }
  }

  function handleComparisonChange(newComparison: string) {
    const selectedComparison = findDataset(newComparison);

    if (!baseline || !selectedComparison) {
      return;
    }

    setComparison(selectedComparison);
    navigateToFootprint(baseline, selectedComparison);
  }

  function handleCompare(baselineReference: string) {
    setIsComparing(true);

    const selectedBaseline = findDataset(baselineReference);
    setBaseline(selectedBaseline);

    const comparisonData = filterComparisonDatasets(selectedBaseline);
    setComparisonDatasets(comparisonData);
  }

  function handleCancelCompare() {
    setIsComparing(false);

    if (baseline) {
      navigateToFootprint(baseline);
    }

    setComparison(undefined);
  }

  function handleComparisonSwap(currentComparisonStage?: string) {
    if (!baseline || !comparison) {
      return;
    }

    const dataset = baseline;
    setBaseline(comparison);
    setComparison(dataset);

    const compatibleComparisons = filterComparisonDatasets(comparison);
    setComparisonDatasets(compatibleComparisons);

    if (featureFlags.ffBugsQ3s1) {
      navigateToFootprint(comparison, dataset, currentComparisonStage);
    } else {
      navigateToFootprint(comparison, dataset);
    }
  }

  return {
    datasets,
    comparisonDatasets,
    baseline,
    comparison,
    isComparing,
    handleBaselineChange,
    handleComparisonChange,
    handleCompare,
    handleCancelCompare,
    handleComparisonSwap,
  };
};

export default useFootprintCompare;
